import { http } from '@noor/api';
import { env } from '@noor/config';
import {
  restaurantBannerResponseSchema,
  restaurantBannerSchema,
  type CreateRestaurantBannerInput,
  type RestaurantBanner,
  type UpdateRestaurantBannerInput,
} from '../model/restaurant-banner';
import { BANNER_PLACEMENT } from '../config/banner';
import type { BusinessListFilter } from '../model/list-params';
import {
  fetchAllBusinessPages,
  normalizeBusinessList,
  unwrapDataPayload,
  type BusinessListResult,
} from './envelope';

const eatsBase = { baseURL: env.VITE_EATS_API_URL };

export interface RestaurantBannerWriteResult {
  banner: RestaurantBanner | null;
  gated: boolean;
}

function unwrapWrite(raw: unknown): RestaurantBannerWriteResult {
  const parsed = restaurantBannerResponseSchema.parse(unwrapDataPayload(raw));
  return { banner: parsed.banner ?? null, gated: parsed.pending_change != null };
}

function toQuery(filter: BusinessListFilter): Record<string, string> {
  return {
    'pagination.limit': String(filter.limit),
    'pagination.offset': String(filter.offset),
  };
}

export const restaurantBannerApi = {
  list(filter: BusinessListFilter): Promise<BusinessListResult<RestaurantBanner>> {
    return http
      .get<unknown>('/api/v1/admin/banners', {
        ...eatsBase,
        query: { ...toQuery(filter), placement: BANNER_PLACEMENT.mainPage },
      })
      .then((raw) => normalizeBusinessList(raw, restaurantBannerSchema, filter));
  },

  listAll(): Promise<RestaurantBanner[]> {
    return fetchAllBusinessPages((filter) => restaurantBannerApi.list(filter));
  },

  async create(input: CreateRestaurantBannerInput): Promise<RestaurantBannerWriteResult> {
    const raw = await http.post<unknown>('/api/v1/admin/banners', input, eatsBase);
    return unwrapWrite(raw);
  },

  async update(
    id: string,
    input: UpdateRestaurantBannerInput,
  ): Promise<RestaurantBannerWriteResult> {
    const raw = await http.patch<unknown>(`/api/v1/admin/banners/${id}`, input, eatsBase);
    return unwrapWrite(raw);
  },

  async remove(id: string): Promise<void> {
    await http.delete<unknown>(`/api/v1/admin/banners/${id}`, eatsBase);
  },
};
